import React from 'react';
import {COLORS, FONTS} from '../styleVars';
import styled from 'styled-components';

interface TypeStyle {
  fontFamily: string;
  fontSize: string;
  fontWeight: number;
  color: string;
}

type TypoConstants = {[name: string]: TypeStyle};

const typoConstants: TypoConstants = {
  headline: {
    fontFamily: FONTS.PTSans,
    fontSize: FONTS['24'],
    fontWeight: FONTS.regular,
    color: COLORS.black,
  },
  title1: {
    fontFamily: FONTS.PTSans,
    fontSize: FONTS['20'],
    fontWeight: FONTS.regular,
    color: COLORS.white,
  },
  title2: {
    fontFamily: FONTS.PTSans,
    fontSize: FONTS['20'],
    fontWeight: FONTS.regular,
    color: COLORS.darkestGrey,
  },
  subheading: {
    fontFamily: FONTS.PTSans,
    fontSize: FONTS['16'],
    fontWeight: FONTS.regular,
    color: COLORS.black,
  },
  body1: {
    fontFamily: FONTS.PTSans,
    fontSize: FONTS['14'],
    fontWeight: FONTS.regular,
    color: COLORS.black,
  },
  body2: {
    fontFamily: FONTS.PTSans,
    fontSize: FONTS['14'],
    fontWeight: FONTS.regular,
    color: COLORS.darkerGrey,
  },
  'body-strong': {
    fontFamily: FONTS.PTSans,
    fontSize: FONTS['14'],
    fontWeight: FONTS.bold,
    color: COLORS.black,
  },
  caption1: {
    fontFamily: FONTS.PTSans,
    fontSize: FONTS['12'],
    fontWeight: FONTS.regular,
    color: COLORS.darkerGrey,
  },
  caption2: {
    fontFamily: FONTS.PTSans,
    fontSize: FONTS['12'],
    fontWeight: FONTS.regular,
    color: COLORS.darkGrey,
  },
  'caption-error': {
    fontFamily: FONTS.PTSans,
    fontSize: FONTS['12'],
    fontWeight: FONTS.regular,
    color: COLORS.neonRed,
  },
  display1: {
    fontFamily: FONTS.Oswald,
    fontSize: FONTS['64'],
    fontWeight: FONTS.regular,
    color: COLORS.darkestGrey,
  },
  display2: {
    fontFamily: FONTS.Oswald,
    fontSize: FONTS['42'],
    fontWeight: FONTS.regular,
    color: COLORS.black,
  },
  headline2: {
    fontFamily: FONTS.Oswald,
    fontSize: FONTS['24'],
    fontWeight: FONTS.regular,
    color: COLORS.darkestGrey,
  },
  result: {
    fontFamily: FONTS.Oswald,
    fontSize: FONTS['20'],
    fontWeight: FONTS.regular,
    color: COLORS.black,
  },
  'result-running': {
    fontFamily: FONTS.Oswald,
    fontSize: FONTS['20'],
    fontWeight: FONTS.regular,
    color: COLORS.fupaSecondary,
  },
  subheading2: {
    fontFamily: FONTS.Oswald,
    fontSize: FONTS['16'],
    fontWeight: FONTS.regular,
    color: COLORS.darkestGrey,
  },
  'button-primary': {
    fontFamily: FONTS.Oswald,
    fontSize: FONTS['14'],
    fontWeight: FONTS.regular,
    color: COLORS.white,
  },
  'button-secondary': {
    fontFamily: FONTS.Oswald,
    fontSize: FONTS['14'],
    fontWeight: FONTS.regular,
    color: COLORS.fupaSecondary,
  },
  'button-sub': {
    fontFamily: FONTS.Oswald,
    fontSize: FONTS['14'],
    fontWeight: FONTS.regular,
    color: COLORS.darkerGrey,
  },
  'button-disabled': {
    fontFamily: FONTS.Oswald,
    fontSize: FONTS['14'],
    fontWeight: FONTS.regular,
    color: COLORS.grey,
  },
  caption3: {
    fontFamily: FONTS.Oswald,
    fontSize: FONTS['12'],
    fontWeight: FONTS.regular,
    color: COLORS.darkerGrey,
  },
  'caption3-active': {
    fontFamily: FONTS.Oswald,
    fontSize: FONTS['12'],
    fontWeight: FONTS.regular,
    color: COLORS.fupaSecondary,
  },
};

type TypoContainerProps = {typeStyle: TypeStyle};

const TypoContainer = styled.span<TypoContainerProps>`
  font-family: ${({typeStyle}) => typeStyle.fontFamily};
  font-size: ${({typeStyle}) => typeStyle.fontSize};
  font-weight: ${({typeStyle}) => typeStyle.fontWeight};
  color: ${({typeStyle}) => typeStyle.color};
`;

enum componentValues {
  span = 'span',
  h1 = 'h1',
  h2 = 'h2',
  h3 = 'h3',
  h4 = 'h4',
  h5 = 'h5',
  h6 = 'h6',
  p = 'p',
  label = 'label',
}

interface TypographyProps {
  /** Defines the font-styling of the children of Typography. */
  variant: keyof typeof typoConstants;
  /** Text node rendering inside of Typo component */
  children: React.ReactNode;
  /** Defines as which HTML-Element Typography will be rendered. */
  component?: keyof typeof componentValues;
  /** Override or extend the styles applied to the component. */
  className?: string;
}

const Typography: React.FC<TypographyProps> = ({
  variant,
  component = getDefaultComponent(variant),
  children,
  className,
}) => {
  const typeStyle = typoConstants[variant];

  // The `as` polymorphic-prop defines how the TypoContainer will be rendered. This is a functionality of styled-components:
  // https://styled-components.com/docs/api#as-polymorphic-prop
  return (
    <TypoContainer className={className} as={component} typeStyle={typeStyle}>
      {children}
    </TypoContainer>
  );
};

const getDefaultComponent = (variant: keyof typeof typoConstants) => {
  switch (variant) {
    case 'headline':
      return componentValues.h2;
    case 'subheading2':
      return componentValues.h3;
    default:
      return componentValues.span;
  }
};

export {Typography};
